import React from 'react'
import { Tag } from 'antd'
import { InvoiceFeesDto } from '../../../api/dto/generated-invoice.dto'
import {
  getBrokerLabel,
  SURECO_PLATFORM_FEE_LABEL,
  THE_CASON_GROUP_FEE_LABEL,
} from './utils'
import {
  FeeCell,
  FeeHeadCell,
  FeeRow,
  FeesWrap,
  FeeTable,
  HeadItem,
  HeadStrip,
  MonthlyCalc,
  SectionTitle,
  StructureTagWrap,
  TotalCard,
  TotalHint,
  TotalLabel,
  TotalValue,
} from './CalculatedFeesSummary.styles'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const formatCents = (cents: number | null): string =>
  typeof cents === 'number' && Number.isFinite(cents)
    ? currencyFormatter.format(cents / 100)
    : '—'

interface CalculatedFeesSummaryProps {
  fees: InvoiceFeesDto
}

/**
 * Single-client fee breakdown for the generated-invoice drawer in a compact
 * layout: a one-line headcount strip, a tight Item/PEPM/Fee table, and a slim
 * charged-total bar. Numbers come from the fee inputs captured on the invoice at
 * generate time (deterministic — what was charged, not a live re-price). SureCo,
 * Broker, and The Cason Group are all charged and flow into the total.
 */
const CalculatedFeesSummary: React.FC<CalculatedFeesSummaryProps> = ({
  fees,
}) => {
  const brokerLabel = getBrokerLabel(fees.brokerName)
  const showTcgFee = fees.tcgFeeCents !== 0
  const chargedTotalCents =
    fees.surecoFeeCents + fees.brokerFeeCents + fees.tcgFeeCents
  const billable = fees.billableEmployeeCount

  // Monthly fee = PEPM × billable headcount. Show the value with that derivation
  // beneath it, but only when there is a real PEPM (skip the "— × N" noise).
  const renderMonthly = (feeCents: number | null, pepmCents: number | null) => {
    const hasPepm = typeof pepmCents === 'number' && Number.isFinite(pepmCents)
    return (
      <MonthlyCalc>
        {formatCents(feeCents)}
        {hasPepm ? (
          <small>
            {formatCents(pepmCents)} × {billable}
          </small>
        ) : null}
      </MonthlyCalc>
    )
  }

  return (
    <FeesWrap>
      <HeadStrip>
        <HeadItem>
          Structure{' '}
          {fees.billingStructure ? (
            <StructureTagWrap>
              <Tag
                color={fees.billingStructure === 'Eligible' ? 'blue' : 'green'}
              >
                {fees.billingStructure}
              </Tag>
            </StructureTagWrap>
          ) : (
            <b>—</b>
          )}
        </HeadItem>
        <HeadItem>
          Enrolled <b>{fees.enrollmentCount}</b>
        </HeadItem>
        <HeadItem>
          Eligible <b>{fees.eligibleEmployeeCount}</b>
        </HeadItem>
        <HeadItem>
          Min EEs <b>{fees.minimumBillableEes ?? 0}</b>
        </HeadItem>
        <HeadItem data-accent="true">
          Billable <b>{fees.billableEmployeeCount}</b>
        </HeadItem>
      </HeadStrip>

      <SectionTitle>
        Rates &amp; fees (× {fees.billableEmployeeCount} billable)
      </SectionTitle>
      <FeeTable role="table" aria-label="Fees by type">
        <FeeRow role="row">
          <FeeHeadCell role="columnheader">Fee</FeeHeadCell>
          <FeeHeadCell role="columnheader">PEPM</FeeHeadCell>
          <FeeHeadCell role="columnheader">Monthly</FeeHeadCell>
        </FeeRow>

        <FeeRow role="row">
          <FeeCell role="rowheader">{SURECO_PLATFORM_FEE_LABEL}</FeeCell>
          <FeeCell role="cell">{formatCents(fees.surecoPepmCents)}</FeeCell>
          <FeeCell role="cell" $accent $strong>
            {renderMonthly(fees.surecoFeeCents, fees.surecoPepmCents)}
          </FeeCell>
        </FeeRow>

        <FeeRow role="row">
          <FeeCell role="rowheader">{brokerLabel}</FeeCell>
          <FeeCell role="cell">{formatCents(fees.brokerPepmCents)}</FeeCell>
          <FeeCell role="cell" $accent $strong>
            {renderMonthly(fees.brokerFeeCents, fees.brokerPepmCents)}
          </FeeCell>
        </FeeRow>

        {showTcgFee ? (
          <FeeRow role="row">
            <FeeCell role="rowheader">{THE_CASON_GROUP_FEE_LABEL}</FeeCell>
            <FeeCell role="cell">{formatCents(fees.tcgPepmCents)}</FeeCell>
            <FeeCell role="cell" $accent $strong>
              {renderMonthly(fees.tcgFeeCents, fees.tcgPepmCents)}
            </FeeCell>
          </FeeRow>
        ) : null}
      </FeeTable>

      <TotalCard>
        <TotalLabel>
          Charged ({SURECO_PLATFORM_FEE_LABEL} + {brokerLabel}
          {showTcgFee ? ` + ${THE_CASON_GROUP_FEE_LABEL}` : ''})
        </TotalLabel>
        <TotalValue>{formatCents(chargedTotalCents)}</TotalValue>
      </TotalCard>
      <TotalHint>
        {formatCents(fees.surecoFeeCents)} + {formatCents(fees.brokerFeeCents)}
        {showTcgFee ? ` + ${formatCents(fees.tcgFeeCents)}` : ''} ={' '}
        {formatCents(chargedTotalCents)}
      </TotalHint>
    </FeesWrap>
  )
}

export { CalculatedFeesSummary }
